import { handleAxiosError } from '@/api/helpers';
import { SuppressedDomain } from '@/client/campaigns/types/suppressed-domains.type';
import { DOMAIN_REGEX, LoadingStatuses } from '@/common/constants';
import {
  useSuppressedDomains,
  useUpdateSuppressedDomainsList,
} from '@/hooks/query/suppressed-domains.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { DomainInput } from '@/ui/domain-input';
import { FlexContainer } from '@/ui/styled-ui';
import { TrashIcon } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import moment from 'moment';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledList = styled(Card)`
  &.p-card {
    border-radius: var(--medium-border-radius);
    border: 1px solid var(--gray-dark);
    box-shadow: none;

    .p-card-body {
      padding: var(--big-padding);
    }

    .p-card-content {
      display: flex;
      flex-direction: column;
      gap: var(--default-padding);
      padding: 0;
    }

    h2 {
      font-size: var(--heading-small-font-size);
      line-height: var(--heading-small-line-height);
      color: var(--black-main);
    }

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      color: var(--black-main);

      &.error {
        font-size: var(--xsmall-font-size);
        line-height: var(--xsmall-line-height);
        color: var(--red-main);
        margin-top: var(--xxsmall-padding) !important;
      }
    }
  }
  .domains-list {
    margin-top: var(--small-padding);
  }
`;

const StyledDomainItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: var(--beige-main);
  border-radius: var(--xsmall-border-radius);
  margin-bottom: var(--small-padding);
  border: 1px solid var(--gray-dark);

  .domain-info {
    flex: 1;
    width: 100%;

    .domain-name {
      font-weight: 600;
      font-size: var(--medium-font-size);
      color: var(--black-main);
      margin-bottom: var(--xsmall-padding);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .suppression-info {
      font-size: var(--xsmall-font-size);
    }
  }

  .delete-button {
    color: var(--black-main);
    cursor: pointer;
    padding: var(--xsmall-padding);
    border-radius: var(--small-border-radius);

    &:hover {
      path {
        fill: var(--red-main);
      }
      color: var(--red-main);
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    }
  }
`;

export const SuppressionList: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentUser = useAppSelector(selectCurrentUser);
  const [error, setError] = useState<string | null>(null);
  const [isChanged, setIsChanged] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [domainsCached, setDomainsCached] = useState<SuppressedDomain[]>([]);
  const [domains, setDomains] = useState<SuppressedDomain[]>([]);

  const {
    suppressedDomains,
    isLoading: isLoadingSuppressedDomains,
    refetch,
  } = useSuppressedDomains();
  const { update: updateDomains, isLoading: isUpdating } =
    useUpdateSuppressedDomainsList();

  useEffect(() => {
    setDomains(suppressedDomains?.result || []);
    setDomainsCached(suppressedDomains?.result || []);
  }, [suppressedDomains?.result]);

  useEffect(() => {
    setIsChanged(!isEqual(domains, domainsCached));
  }, [domains, domainsCached]);

  const handleAddDomain = async () => {
    const trimmedDomain = newDomain.trim();
    if (!DOMAIN_REGEX.test(trimmedDomain)) {
      setError(t('globalSettings.suppressionList.invalidDomain'));
      return;
    }

    if (trimmedDomain) {
      const newDomainItem: SuppressedDomain = {
        domain: trimmedDomain,
      };

      setDomains([newDomainItem, ...domains]);
      setNewDomain('');
    }
  };

  const handleDeleteDomain = (index: number) => {
    setDomains(domains.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      await updateDomains({ domains });
      setIsChanged(false);
      toast?.success(
        t('toast.success'),
        t('globalSettings.suppressionList.saved'),
      );
      await refetch();
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleCancel = () => {
    setDomains(domainsCached);
  };

  return isLoadingSuppressedDomains ? (
    <FlexContainer direction="column" className="mt-5">
      <ProgressSpinner />
    </FlexContainer>
  ) : (
    <StyledList>
      <FlexContainer justify="space-between" align="center">
        <FlexContainer direction="column" align="flex-start" gap={6}>
          <h2 className="m-0">{t('globalSettings.suppressionList')}</h2>
          <p className="m-0">
            {t('globalSettings.suppressionListDescription')}
          </p>
        </FlexContainer>
        <FlexContainer gap={12} justify="flex-end">
          {isChanged && (
            <AppButton
              type="text"
              severity="secondary"
              label={t('globalSettings.cancelChanges')}
              onClick={handleCancel}
              isDisabled={isUpdating}
            />
          )}
          <AppButton
            label={t('globalSettings.saveList')}
            onClick={handleSave}
            isDisabled={!isChanged}
            state={isUpdating ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
          />
        </FlexContainer>
      </FlexContainer>

      <div>
        <DomainInput
          value={newDomain}
          onChange={(value) => {
            setNewDomain(value);
            if (error) setError(null);
          }}
          onAdd={handleAddDomain}
          placeholder={t('globalSettings.enterDomainPlaceholder')}
          message={error ? { text: error, variant: 'error' as const } : null}
          disabled={isUpdating}
        />

        {domains?.length > 0 && (
          <div className="domains-list">
            {domains?.map((domain, index) => (
              <StyledDomainItem key={domain.id ?? domain.domain}>
                <div className="domain-info">
                  <div className="domain-name">{domain.domain}</div>
                  <div className="suppression-info">
                    {t('globalSettings.suppressedBy', {
                      user: domain.createdBy?.name || currentUser?.name,
                      date: moment(domain.created).format(DateFormats.DATEONLY),
                    })}
                  </div>
                </div>

                <div
                  className={`delete-button ${isUpdating ? 'disabled' : ''}`}
                  onClick={() => !isUpdating && handleDeleteDomain(index)}
                >
                  <TrashIcon />
                </div>
              </StyledDomainItem>
            ))}
          </div>
        )}
      </div>
    </StyledList>
  );
};
